/**
 * IdleSessionGate — the 30-minute sign-out, wired into Explorer.
 *
 * Warns at 28 minutes, replaces the app at 30, and in one tab only, revokes the
 * session.
 *
 * Nothing here closes a tab. Console opened this one with `window.open` but
 * threw away the reference, and the reference would not survive a Console
 * reload anyway. So Explorer locks itself, by watching a cookie the whole
 * family of tabs shares.
 *
 * The app is unmounted rather than blurred. A blur removes nothing: the
 * response data would still be in the page for anybody with the developer tools
 * open. With the tree gone there is nothing behind the lock screen to read.
 *
 * It also carries a context. App puts the current survey's name on the idle
 * session, so if this tab locks, its own lock screen can say which survey it
 * was — the only thing that lets a person decide whether it is worth signing
 * back in for.
 */

import React, { useCallback } from 'react';
import { useIdleSession, IdleLockScreen, IdleWarningBanner } from '@cloudsam/ui';
import { useAuth } from '../contexts/AuthContext';
import { IdleContext } from '../contexts/IdleContext';
import { idleClock, IDLE_LOCK_MINUTES } from '../idle-session';
import hanko from '../hanko-init';
import SamLogo from './SamLogo';

export default function IdleSessionGate({ children }) {
    const { isAuthenticated } = useAuth();

    const handleLock = useCallback(async ({ owned }) => {
        // Only the tab that claimed the sign-out talks to the server. The
        // others have already locked and have nothing to do.
        if (!owned) return;
        try {
            await hanko.user.logout();
        } catch (err) {
            // The screen is locked either way, and the server-side idle check
            // is the backstop for a revoke that did not land.
            console.warn('[IdleSessionGate] session revoke failed');
        }
    }, []);

    const idle = useIdleSession({
        clock: idleClock,
        enabled: Boolean(isAuthenticated),
        onLock: handleLock,
    });

    if (idle.isLocked) {
        return (
            <IdleLockScreen
                logo={<SamLogo size={56} showWordmark />}
                appName="Explorer"
                reason={idle.reason}
                saving={idle.saving}
                saveResults={idle.saveResults}
                contextLabel={idle.contextLabel}
                idleMinutes={IDLE_LOCK_MINUTES}
            />
        );
    }

    return (
        <IdleContext.Provider value={idle}>
            {children}
            {idle.isWarning && (
                <IdleWarningBanner
                    secondsUntilLock={idle.secondsUntilLock}
                    saving={idle.saving}
                    onStaySignedIn={idle.staySignedIn}
                />
            )}
        </IdleContext.Provider>
    );
}
